import { FC, useContext, useEffect, useState, useMemo } from 'react';
import { MultiSelect, SelectOptionType, useToast } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../../Localization';
import { ConfigurationContext } from '../../../../Context';
import { usePost } from '../../../../API';
import { useAsyncEffect } from '../../../../Hooks/AsyncEffect';
import { DEFAULT_TAGS } from '../shared/utils';
import { TagsSectionProps } from '../shared/types';

/**
 * Tags section with tag management using MultiSelect component
 */
export const TagsSection: FC<TagsSectionProps> = ({ caseInfo }) => {
  const L = useLocalizedStrings();
  const config = useContext(ConfigurationContext);
  const { toast } = useToast();
  const setCaseTags = usePost('/support/set_case_tags');
  const getProgramTags = usePost('/support/get_program_tags');

  // Tag management state
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Initialize tags from caseInfo metadata
  useEffect(() => {
    if (caseInfo.metadata && typeof caseInfo.metadata === 'object' && !Array.isArray(caseInfo.metadata)) {
      setTags(caseInfo.metadata.tags ?? []);
    }
  }, [caseInfo.metadata]);

  // Fetch program tags
  useAsyncEffect(async () => {
    const result = await getProgramTags({});
    // Merge program and default tags, remove duplicates, and sort alphabetically
    const mergedTags = Array.from(
      new Set([
        ...(Array.isArray(result?.tags)
          ? result.tags.filter((tag): tag is string => typeof tag === 'string' && tag != null)
          : []),
        ...DEFAULT_TAGS,
      ])
    ).sort((a, b) => a.localeCompare(b));
    setAvailableTags(mergedTags);
  }, []);

  const handleTagsChange = async (newTags: string[]) => {
    const previousTags = tags;
    setTags(newTags);

    try {
      await setCaseTags({
        caseId: caseInfo.uid,
        tags: newTags
      });
    } catch (error) {
      // Revert on failure
      setTags(previousTags);
      toast({
        description: L.support.tags.problem_updating_tags,
        variant: 'error'
      });
    }
  };

  const userCanCreateNewTags = config?.roles?.includes('admin');

  // Convert string arrays to SelectOptionType arrays
  const tagOptions: SelectOptionType[] = useMemo(() =>
    availableTags.map(tag => ({ label: tag, value: tag })),
  [availableTags]
  );

  const selectedTags: SelectOptionType[] = useMemo(() =>
    tags.map(tag => ({ label: tag, value: tag })),
  [tags]
  );

  const handleChange = (newSelectedTags: SelectOptionType[]) => {
    const newTags = newSelectedTags.map(option => option.value);
    handleTagsChange(newTags);
  };

  const handleAddCustomOption = (option: SelectOptionType) => {
    // Add the custom tag to available tags so it appears in future searches
    setAvailableTags(prev => {
      const newTags = [...prev, option.value];
      return Array.from(new Set(newTags)).sort((a, b) => a.localeCompare(b));
    });
  };

  return (
    <div className="pb-6 border-b border-gray-200 min-w-0">
      <MultiSelect
        label={L.support.fields.tags}
        description={L.support.tags.add_tag_placeholder}
        placeholder={L.support.tags.add_tag_placeholder}
        options={tagOptions}
        value={selectedTags}
        onChange={handleChange}
        showTags={true}
        customAllowed={userCanCreateNewTags}
        onAddCustomOption={handleAddCustomOption}
        searchable={true}
      />
    </div>
  );
};
